/**
 * CraterDropSkillEffect — Earth tree stone_fist path active (crater_drop).
 *
 * Phased skill: wind-up (400ms) → active (slam + zone creation) → recovery → cooldown (long).
 *
 * On hit:
 * - Radial AoE damage (22 base) to all enemies within `craterRadiusTiles` of the target point
 * - Creates a ZoneSystem 'crater' zone at the target point
 * - Crater zone: radiusTiles=2, durationMs=8000, speedMultiplier=0.5
 * - Wide Impact adds craterRadiusTiles delta (+0.5) and craterDurationMs delta (+3000)
 * - Sinkhammer adds entryDamage to the zone config (enemies take damage on entry)
 *
 * Target point resolved by the game layer via targeting.getTargetPosition(maxRangeTiles).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Fork 2 — Crater Sub-Path
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CraterDropDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly targeting: {
    /**
     * Returns the crater placement position, clamped to maxRangeTiles from the player.
     * The game layer handles cursor-vs-facing resolution and range clamping.
     */
    getTargetPosition(maxRangeTiles: number): { x: number; y: number };
    /** Returns enemy IDs within radiusTiles of the given position. */
    getEnemiesInRadius(position: { x: number; y: number }, radiusTiles: number): string[];
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
  };
  readonly zones: {
    create(config: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      speedMultiplier: number;
      entryDamage?: number;
      sourceSkillId: string;
    }): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CraterDropConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  /** Base AoE damage at impact. */
  readonly baseDamage: number;
  /** Base crater zone radius in tiles. */
  readonly baseCraterRadiusTiles: number;
  /** Base crater zone lifetime in ms. */
  readonly baseCraterDurationMs: number;
  /** Crater zone speed multiplier (0.5 = 50% speed). */
  readonly craterSpeedMultiplier: number;
  /** Max placement range from player in tiles. */
  readonly maxPlacementRangeTiles: number;
}

const DEFAULT_CONFIG: CraterDropConfig = {
  windupMs: 400,               // flagged for design decision; using 400ms
  recoveryMs: 150,
  cooldownMs: 2000,            // long cooldown tier
  baseDamage: 22,
  baseCraterRadiusTiles: 2,
  baseCraterDurationMs: 8000,
  craterSpeedMultiplier: 0.5,
  maxPlacementRangeTiles: 2,
};

// ---------------------------------------------------------------------------
// Phase sequence
// ---------------------------------------------------------------------------

const PHASE_SEQUENCE: readonly SkillPhase[] = ['windup', 'active', 'recovery', 'cooldown'];

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CraterDropSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'crater_drop';

  protected readonly phaseSequence = PHASE_SEQUENCE;

  private readonly deps: CraterDropDeps;
  private readonly config: CraterDropConfig;

  // Per-activation telemetry
  private _targetsHit: string[] = [];
  private _lastDamageDealt = 0;
  private _lastCraterRadiusTiles = 0;
  private _lastCraterDurationMs = 0;
  private _lastZoneId = '';
  private _activationCount = 0;

  constructor(deps: CraterDropDeps, config: Partial<CraterDropConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get targetsHit(): readonly string[] { return this._targetsHit; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get lastCraterRadiusTiles(): number { return this._lastCraterRadiusTiles; }
  get lastCraterDurationMs(): number { return this._lastCraterDurationMs; }
  get lastZoneId(): string { return this._lastZoneId; }
  get activationCount(): number { return this._activationCount; }

  // --- PhasedSkillEffect overrides ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 16;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override onActivate(): void {
    this._targetsHit = [];
    this._lastDamageDealt = 0;
    this._lastCraterRadiusTiles = 0;
    this._lastCraterDurationMs = 0;
    this._lastZoneId = '';
    this._activationCount++;
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase !== 'active') return;

    // Resolve crater parameters with upgrade deltas
    const radiusDelta = this.deps.resolver.getAdditiveEffect('crater_drop', 'craterRadiusTiles');
    const durationDelta = this.deps.resolver.getAdditiveEffect('crater_drop', 'craterDurationMs');
    const effectiveRadiusTiles = this.config.baseCraterRadiusTiles + radiusDelta;
    const effectiveDurationMs = this.config.baseCraterDurationMs + durationDelta;

    this._lastCraterRadiusTiles = effectiveRadiusTiles;
    this._lastCraterDurationMs = effectiveDurationMs;

    // Get target position (clamped to max range by the targeting layer)
    const targetPos = this.deps.targeting.getTargetPosition(this.config.maxPlacementRangeTiles);

    // Apply radial AoE damage to enemies within effective radius
    const targets = this.deps.targeting.getEnemiesInRadius(targetPos, effectiveRadiusTiles);
    for (const targetId of targets) {
      this.deps.combat.applyDamage(targetId, this.config.baseDamage, {
        sourceSkillId: 'crater_drop',
      });
    }
    this._targetsHit = [...targets];
    this._lastDamageDealt = this.config.baseDamage * targets.length;

    // Create crater zone
    const zoneConfig: {
      type: string;
      position: { x: number; y: number };
      radiusTiles: number;
      durationMs: number;
      speedMultiplier: number;
      entryDamage?: number;
      sourceSkillId: string;
    } = {
      type: 'crater',
      position: targetPos,
      radiusTiles: effectiveRadiusTiles,
      durationMs: effectiveDurationMs,
      speedMultiplier: this.config.craterSpeedMultiplier,
      sourceSkillId: 'crater_drop',
    };

    // Sinkhammer: add entry damage to zone config
    if (this.deps.resolver.hasEffect('crater_drop', 'entryDamage')) {
      zoneConfig.entryDamage =
        this.deps.resolver.getAdditiveEffect('crater_drop', 'entryDamage');
    }

    this._lastZoneId = this.deps.zones.create(zoneConfig);
    this.markHitApplied();
  }
}
